import { useParams } from "react-router-dom";
import Loader from "../../../../../Components/Loader";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../../axiosInterceptor";
import { useFormik } from "formik";
import { dispatchSchema } from "../../../../../Schema/Schema";
import SectionHeader from "./SectionHeader";
import ProductCard from "./productCard";
import EditDropdown from "./editDropDown";
import DeleteModel from "../../../../../Components/DeleteModel";
import { deleteItem } from "../../../../../utils/apiUtils";
import StatusBadge from "../../../../../Components/StatusBadge";

const Dispatch = () => {
  const { id } = useParams();
  const [dispatchData, setDispatchData] = useState({});
  const [loading, setLoading] = useState(true);
  const [itemsQuantity, setItemsQuantity] = useState({});
  const [formEnable, setFormEnable] = useState(false);
  const [editID, setEditID] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState();
  const [isDeleting, setIsDeleting] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false)
  const fetchDispatchData = async () => {
    try {
      const response = await axiosInstance.get(`dispatch/${id}`);
      if (response.status === 200) {
        const data = response.data?.data;
        setDispatchData(data);

        const initialQuantities = {};
        data?.orderItems?.forEach((item) => {
          initialQuantities[item.id] = item.remainingQty || item.quantity || 0;
        });
        setItemsQuantity(initialQuantities);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDispatchData();
  }, [id]);

  const formik = useFormik({
    initialValues: {
      orderId: id,
      trackingId: "",
      trackingUrl: "",
      items: [],
    },
    validationSchema: dispatchSchema,
    onSubmit: async (values, { resetForm }) => {
      const items = dispatchData.orderItems
        .map((item) => ({ orderItemId: item.id, quantity: itemsQuantity[item.id] || 0 }))
        .filter((i) => i.quantity > 0);

      if (!items.length) {
        toast.error("Please select at least one product to dispatch.");
        return;
      }

      const payload = { ...values, orderId: dispatchData?.id, items };
      const editPayload = { trackingId: values.trackingId, trackingUrl: values.trackingUrl };

      try {
        setBtnLoading(true);
        const response = editID
          ? await axiosInstance.put(`/dispatch/${editID}`, editPayload)
          : await axiosInstance.post("/dispatch", payload);

        if (response.status === 200) {
          toast.success(response.data?.message);
          resetForm();
          setEditID(null);
          setFormEnable(false);
          fetchDispatchData();
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "Dispatch failed");
      } finally {
        setBtnLoading(false);
      }
    },
  });

  const handleEdit = (dispatchId, trackingId, trackingUrl) => {
    setFormEnable(true);
    setEditID(dispatchId);
    formik.setFieldValue("trackingId", trackingId);
    formik.setFieldValue("trackingUrl", trackingUrl);
  };

  const handleCancel = () => {
    formik.resetForm();
    setEditID(null);
    setFormEnable(false);
  };

  const handleDelete = (id) => {
    setDeleteItemId(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      const res = await deleteItem("dispatch", deleteItemId, fetchDispatchData, []);
      setDeleteItemId(null);
      setDeleteModalOpen(false);
      return res;
    } catch (err) {
      throw err;
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="col-md-12">
      <div className="row">
<div className={`col-md-${dispatchData.orderItems?.length > 0 ? "6" : "12"}`}>
          <div className="card shadow-sm border-0 mb-3">
            <div className="card-header bg-light">
              <SectionHeader
                icon="fa-solid fa-box"
                title="Fulfilled Items"
                color="text-success"
              />
            </div>
          </div>
          {dispatchData.dispatches?.length > 0 ? (
            dispatchData.dispatches.map((dispatch) => (
              <div key={dispatch.id} className="card shadow-sm border-0 mb-3">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <p className="mb-1 fw-bold">Tracking ID: {dispatch.trackingId}</p>
                      <p className="mb-1 fw-bold">
                        Tracking URL:{" "}
                        {dispatch.trackingUrl ? (
                          <a
                            href={dispatch.trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            {dispatch.trackingUrl}
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </p>
                    </div>

                    <EditDropdown
                      onEdit={() =>
                        handleEdit(dispatch.id, dispatch.trackingId, dispatch.trackingUrl)
                      }
                      onDelete={() => handleDelete(dispatch.id)}
                    />
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <span>  <StatusBadge status={dispatch.status} /></span>
                    <small>
                      {new Date(dispatch.createdAt).toLocaleString("en-US", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </small>
                  </div>

                  <hr />

                  <div className="d-flex flex-column gap-2">
                    {dispatch.orderItems.map((oi) => (
                      <ProductCard
                        key={oi.orderItemId}
                        item={{
                          ...oi.orderItem,
                          quantity: oi.quantity,
                          remainingQty: 0,
                        }}
                        editable={false}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-4">
                <i className="fa-solid fa-circle-exclamation fa-2x mb-2 text-danger"></i>
                <p className="mb-0 fw-bold">No Fulfilled Items Found</p>
              </div>
            </div>
          )}
        </div>



        <div className="col-md-6">
          {dispatchData.orderItems?.length > 0 && (
            <div className="card shadow-sm border-0 mb-3">
              <div className="card-header bg-light">
                <SectionHeader icon="fa-solid fa-truck" title="Pending Items" color="text-danger" />
              </div>
              <div className="card-body d-flex flex-column gap-2">
                {dispatchData.orderItems.map((item) => (
                  <ProductCard
                    key={item.id}
                    item={item}
                    itemsQuantity={itemsQuantity}
                    setItemsQuantity={setItemsQuantity}
                    editable
                    dispatches={dispatchData.dispatches}
                  />
                ))}
              </div>
            </div>
          )}

          {(formEnable || dispatchData?.orderItems?.length > 0) && (
            <form onSubmit={formik.handleSubmit} className="card shadow-sm border-0">
              <div className="card-body d-flex flex-column " style={{ gap: "20px" }}>
                <input
                  type="text"
                  id="trackingId"
                  name="trackingId"
                  placeholder="Enter Tracking ID"
                  className="form-control form-control-sm"
                  value={formik.values.trackingId}
                  onChange={formik.handleChange}
                />
                {formik.touched.trackingId && formik.errors.trackingId && (
                  <small className="text-danger">{formik.errors.trackingId}</small>
                )}

                <input
                  type="text"
                  id="trackingUrl"
                  name="trackingUrl"
                  placeholder="Enter Tracking URL"
                  className="form-control form-control-sm"
                  value={formik.values.trackingUrl}
                  onChange={formik.handleChange}
                />
                {formik.touched.trackingUrl && formik.errors.trackingUrl && (
                  <small className="text-danger">{formik.errors.trackingUrl}</small>
                )}

                <div className="d-flex flex-row mt-2 " style={{ gap: "10px" }}>
                  {editID && (
                    <button type="button" className="btn btn-outline-danger w-100" onClick={handleCancel}>
                      Cancel
                    </button>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={btnLoading}
                  >
                    {btnLoading ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    ) : null}
                    {editID ? "Update Dispatch" : "Save Dispatch"}
                  </button>

                </div>
              </div>
            </form>
          )}
        </div>

      </div>
      <DeleteModel
        deleteModalOpen={deleteModalOpen}
        setDeleteModalOpen={() => setDeleteModalOpen(!deleteModalOpen)}
        onConfirm={confirmDelete}
        itemName="Dispatch"
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default Dispatch;